// Shared by the marketing website and the product app. No framework dependency.
// The website owns this module so its standalone Docker build stays self-contained.
import { LANDLORD_GUIDES, guidePath } from "./landlord-guide-index";

const INSIGHT_PATHS = new Set([
  "/insights/",
  "/insights/landlord-guides/",
  ...LANDLORD_GUIDES.map((guide) => guidePath(guide.slug)),
]);

export const MEASUREMENT_ID = "G-RLXK4T69ZB";
export const CONSENT_EVENT = "rentinit:analytics-consent";
const CONSENT_COOKIE = "rentinit_analytics";
const INTERNAL_COOKIE = "rentinit_analytics_internal";
type Consent = "accepted" | "rejected" | null;
type Gtag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: Gtag;
  "ga-disable-G-RLXK4T69ZB"?: boolean;
};
let initialized = false;
let lastPage = "";
let userId: string | null = null;
let pendingLogin = false;

export function isAnalyticsHost(host: string): boolean {
  return [
    "rentinit.ae",
    "www.rentinit.ae",
    "app.rentinit.ae",
    "rentinit.ai",
    "www.rentinit.ai",
  ].includes(host);
}

// Only public marketing routes are eligible. Reports and token-bearing links
// never load the tag. Product paths use a vocabulary, never record IDs or titles.
export function analyticsPath(host: string, path: string): string | null {
  if (!isAnalyticsHost(host)) return null;
  if (host !== "app.rentinit.ae") {
    const canonicalPath = path.endsWith("/") ? path : `${path}/`;
    if (INSIGHT_PATHS.has(canonicalPath)) return canonicalPath;
    return /^(\/(about|pricing|property-management|tenant-screening|privacy|terms|cookie-policy|data-protection|disclaimer)?\/?|\/insights\/weekly-market(\/\d{4}-W\d{2})?\/?|\/insights\/market(\/\d{4}-\d{2})?\/?)$/.test(
      path,
    )
      ? path
      : null;
  }
  if (
    /(^|\/)(callback|logout-callback|sign|pay|financial-score|reset-password|invite|agent-invite|reports|documents)(\/|$)/.test(
      path,
    )
  )
    return null;
  const words = new Set(
    "signin signup signout signin-error verify-email request-password-reset reset-password-success pm agent landlord manager tenant user select-role home dashboard people money chat manage-access properties leads details profile billing success cancel jobs inspections reports maintenance documents settings".split(
      " ",
    ),
  );
  return path
    .split("/")
    .map((part) => (!part || words.has(part) ? part : ":id"))
    .join("/");
}

export function safePageLocation(href: string): string | null {
  const url = new URL(href);
  const path = analyticsPath(url.hostname, url.pathname);
  if (path === null) return null;
  // Strip query strings and fragments (email, OAuth codes, report tokens, etc.).
  return `${url.origin}${path}`;
}

function cookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((value) => value.startsWith(`${name}=`))
    ?.split("=")[1];
}

function setCookie(name: string, value: string, maxAge = 15552000) {
  const domain = location.hostname.endsWith("rentinit.ae")
    ? "; Domain=rentinit.ae"
    : "; Domain=rentinit.ai";
  document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure${domain}`;
}

export function getConsent(): Consent {
  const value = cookie(CONSENT_COOKIE);
  return value === "accepted" || value === "rejected" ? value : null;
}

export function isInternal(): boolean {
  return cookie(INTERNAL_COOKIE) === "1";
}

export function prepareAnalytics(): boolean {
  if (!isAnalyticsHost(location.hostname)) return false;
  const internal = new URLSearchParams(location.search).get(
    "analytics_internal",
  );
  if (internal === "1" || internal === "0")
    setCookie(INTERNAL_COOKIE, internal);
  return analyticsPath(location.hostname, location.pathname) !== null;
}

function permitted(): boolean {
  return (
    getConsent() === "accepted" &&
    !isInternal() &&
    analyticsPath(location.hostname, location.pathname) !== null
  );
}

function currentPage() {
  const params = new URLSearchParams(location.search);
  const campaign: Record<string, string> = {};
  for (const [query, field] of [
    ["utm_source", "campaign_source"],
    ["utm_medium", "campaign_medium"],
    ["utm_campaign", "campaign_name"],
  ] as const) {
    const value = params.get(query);
    if (value && /^[a-zA-Z0-9_. -]{1,100}$/.test(value))
      campaign[field] = value;
  }
  return {
    page_location: safePageLocation(location.href),
    page_title:
      location.hostname === "app.rentinit.ae"
        ? `Rentinit app ${analyticsPath(location.hostname, location.pathname)}`
        : document.title,
    // Referrer host is sufficient for source attribution; never send its query or path.
    page_referrer: document.referrer ? new URL(document.referrer).origin : "",
    ...campaign,
  };
}

function start(): Gtag | undefined {
  const win = window as AnalyticsWindow;
  win[`ga-disable-${MEASUREMENT_ID}`] = !permitted();
  if (!permitted()) return;
  if (!initialized) {
    win.dataLayer = win.dataLayer || [];
    win.gtag = function () {
      // Use Google's documented Arguments command format, not a GTM array.
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer!.push(arguments);
    };
    win.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    win.gtag("js", new Date());
    win.gtag("config", MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_domain: "auto",
      user_id: userId,
      ...currentPage(),
    });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);
    initialized = true;
  }
  return win.gtag;
}

export function setConsent(value: Exclude<Consent, null>) {
  setCookie(CONSENT_COOKIE, value);
  const win = window as AnalyticsWindow;
  win[`ga-disable-${MEASUREMENT_ID}`] = !permitted();
  if (initialized)
    win.gtag?.("consent", "update", {
      analytics_storage: value === "accepted" ? "granted" : "denied",
    });
  if (value === "rejected") {
    for (const entry of document.cookie.split("; ")) {
      const name = entry.split("=")[0];
      if (name && /^_ga($|_)/.test(name)) {
        setCookie(name, "", 0);
        document.cookie = `${name}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
      }
    }
    lastPage = "";
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function identifyAnalyticsUser(id: string | null) {
  // Opaque IdentityServer UUID only. Never names, email, phone or UAE Pass IDs.
  userId =
    id && /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id) ? id : null;
  try {
    if (initialized && permitted())
      (window as AnalyticsWindow).gtag?.("config", MEASUREMENT_ID, {
        user_id: userId,
        send_page_view: false,
      });
  } catch {
    /* A blocked analytics provider must not break sign-in or sign-out. */
  }
}

export function recordSuccessfulLogin(id: string) {
  identifyAnalyticsUser(id);
  // The OAuth callback URL is excluded. Emit after the app navigates away.
  try {
    pendingLogin = getConsent() === "accepted" && !isInternal();
  } catch {
    pendingLogin = false;
  }
}

export function trackPage() {
  try {
    prepareAnalytics();
    const tag = start();
    if (!tag) {
      lastPage = "";
      return;
    }
    if (lastPage === location.href) return;
    lastPage = location.href;
    tag("config", MEASUREMENT_ID, {
      ...currentPage(),
      user_id: userId,
      send_page_view: false,
    });
    tag("event", "page_view", { ...currentPage(), send_to: MEASUREMENT_ID });
    if (pendingLogin) {
      pendingLogin = false;
      trackConversion("login");
    }
  } catch {
    /* Analytics must never interrupt navigation or authentication. */
  }
}

export type Conversion =
  | "sign_up"
  | "login"
  | "demo_booked"
  | "signup_click"
  | "app_open"
  | "whatsapp_click";
export function trackConversion(event: Conversion) {
  try {
    const tag = start();
    if (tag) tag("event", event, { ...currentPage(), send_to: MEASUREMENT_ID });
  } catch {
    /* The underlying action succeeds independently of measurement. */
  }
}

export function trackMarketingLink(event: MouseEvent) {
  if (!(event.target instanceof Element)) return;
  const anchor = event.target.closest("a");
  if (!anchor) return;
  const url = new URL(anchor.href);
  if (url.hostname === "wa.me") trackConversion("whatsapp_click");
  if (url.hostname === "app.rentinit.ae")
    trackConversion(url.pathname === "/signup" ? "signup_click" : "app_open");
}
